import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/constants';

const PushPierCalculator = ({ onBack, currentUser }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    wallLength: 0,
    stories: null,
    exteriorMaterial: null,
    wallType: null,
    foundationType: null
  });
  const [results, setResults] = useState(null);

  // PP288 Pier Capacity (to bedrock, 48" sleeve)
  const pierCapacityRated = 31200; // lbs (rated)
  const pierCapacityDesign = 15600; // lbs (50% safety factor for engineering design)

  // Base Load per Story (lbs/ft) - Updated to match engineer specification
  const storyLoads = {
    basement: 4000,
    floor1: 5000,
    floor2: 4000,
    floor3: 4000  // Changed from 3500 to 4000 to match engineer calculation
  };

  // Cladding Multipliers
  const claddingMultipliers = {
    'Vinyl': 1.00,
    'Stucco': 1.05,
    'Brick': 1.15,
    'Stone': 1.30
  };

  // Foundation Type Multipliers
  const foundationMultipliers = {
    'Poured Concrete': 1.00,
    'Block': 1.10,
    'Wood': 0.85
  };

  // Wall Type Adjustments
  const wallTypeAdjustments = {
    'Roof-bearing': 1.00,
    'Gable End': 0.75
  };

  // Foundation Wall Height Multiplier (NEW)
  const wallHeightMultiplier = 1.15; // For 8-9 ft foundation walls

  const calculateResults = () => {
    // Step 1: Calculate base loads based on stories (Engineer method: practical floor calculation)
    let totalLoad = 0;
    
    // Engineer approach: For "3 stories including basement" = basement + ground + 2nd floor
    if (formData.stories >= 1) {
      totalLoad += storyLoads.basement;
      totalLoad += storyLoads.floor1;
    }
    if (formData.stories >= 2) totalLoad += storyLoads.floor2;
    // Note: For 3 stories "including basement", we have basement + 1st + 2nd = 3 levels total

    // Step 2: Apply wall type multiplier first
    const wallTypeMultiplier = wallTypeAdjustments[formData.wallType] || 1.0;
    totalLoad = totalLoad * wallTypeMultiplier;

    // Step 3: Apply all remaining multipliers
    const claddingMultiplier = claddingMultipliers[formData.exteriorMaterial] || 1.0;
    const foundationMultiplier = foundationMultipliers[formData.foundationType] || 1.0;
    
    console.log('Debug - Calculation values:', {
      totalLoad,
      wallTypeMultiplier,
      claddingMultiplier,
      foundationMultiplier,
      wallHeightMultiplier,
      exteriorMaterial: formData.exteriorMaterial,
      foundationType: formData.foundationType
    });
    
    // Apply all multipliers including the new wall height multiplier
    const adjustedLoad = totalLoad * claddingMultiplier * foundationMultiplier * wallHeightMultiplier;

    // Step 4: Calculate required piers using DESIGN capacity (with safety factor)
    const requiredPiers = Math.ceil(adjustedLoad / pierCapacityDesign);

    // Step 5: Calculate spacing
    let pierSpacing = formData.wallLength / requiredPiers;
    // Clamp spacing between 3.5 ft and 6.0 ft
    const originalSpacing = pierSpacing;
    pierSpacing = Math.max(3.5, Math.min(6.0, pierSpacing));

    // If spacing was clamped, recalculate pier count
    const finalPiers = pierSpacing === originalSpacing ? 
                      requiredPiers : 
                      Math.ceil(formData.wallLength / pierSpacing);

    // Generate calculation notes
    const notes = [];
    notes.push('Engineer-level calculation with safety factors applied');
    notes.push(`Cladding multiplier: ${claddingMultiplier.toFixed(2)}x (${formData.exteriorMaterial})`);
    notes.push(`Foundation multiplier: ${foundationMultiplier.toFixed(2)}x (${formData.foundationType})`);
    notes.push(`Wall type multiplier: ${wallTypeMultiplier.toFixed(2)}x (${formData.wallType})`);
    notes.push(`Foundation height multiplier: ${wallHeightMultiplier.toFixed(2)}x (8-9 ft walls)`);
    notes.push(`Design load capacity: ${pierCapacityDesign.toLocaleString()} lbs (50% safety factor)`);
    
    if (pierSpacing === 3.5) {
      notes.push('Spacing set to minimum allowable (3.5 ft)');
    } else if (pierSpacing === 6.0) {
      notes.push('Spacing set to maximum allowable (6.0 ft)');
    }

    setResults({
      requiredPiers: finalPiers,
      pierSpacing: pierSpacing.toFixed(1),
      totalLoad: Math.round(totalLoad),
      adjustedLoad: Math.round(adjustedLoad),
      pierCapacityRated,
      pierCapacityDesign,
      notes,
      wallLength: formData.wallLength
    });
  };

  const nextStep = () => {
    if (currentStep === 5) {
      calculateResults();
    }
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(Math.max(1, currentStep - 1));
  };

  const resetCalculator = () => {
    setCurrentStep(1);
    setFormData({
      wallLength: 0,
      stories: null,
      exteriorMaterial: null,
      wallType: null,
      foundationType: null
    });
    setResults(null);
  };

  // Render individual steps
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-6">What is the total wall length being supported?</h2>
            <div className="mb-6">
              <input
                type="number"
                value={formData.wallLength || ''}
                onChange={(e) => setFormData({...formData, wallLength: parseFloat(e.target.value) || 0})}
                className="text-3xl font-bold text-center border-2 border-gray-300 rounded-xl py-6 px-8 w-64"
                placeholder="Enter feet"
                min="1"
                max="500"
              />
              <div className="text-gray-600 mt-3 text-xl">feet</div>
            </div>
            <button
              onClick={nextStep}
              disabled={!formData.wallLength || formData.wallLength <= 0}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-xl font-bold py-4 px-8 rounded-xl transition-colors"
            >
              Continue
            </button>
          </div>
        );

      case 2:
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-6">How many stories including the basement?</h2>
            <div className="space-y-4">
              {[
                { value: 1, label: '1 Story (Ground floor only)' },
                { value: 2, label: '2 Stories (Basement + Ground floor)' },
                { value: 3, label: '3 Stories (Basement + Ground + Upper)' }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => {setFormData({...formData, stories: option.value}); nextStep();}}
                  className="w-full max-w-lg mx-auto block bg-green-600 hover:bg-green-700 text-white text-lg font-bold py-5 px-6 rounded-xl transition-colors"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-6">What is the exterior material?</h2>
            <div className="space-y-4">
              {[
                { key: 'Vinyl', label: 'Vinyl Siding', multiplier: '1.00x' },
                { key: 'Stucco', label: 'Stucco', multiplier: '1.05x' },
                { key: 'Brick', label: 'Brick Veneer', multiplier: '1.15x' },
                { key: 'Stone', label: 'Stone Veneer', multiplier: '1.30x' }
              ].map((material) => (
                <button
                  key={material.key}
                  onClick={() => {setFormData({...formData, exteriorMaterial: material.key}); nextStep();}}
                  className="w-full max-w-lg mx-auto block bg-orange-600 hover:bg-orange-700 text-white text-lg font-bold py-4 px-6 rounded-xl transition-colors"
                >
                  <div>{material.label}</div>
                  <div className="text-sm font-normal opacity-90">Load multiplier: {material.multiplier}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-6">What type of wall are we supporting?</h2>
            <div className="space-y-4">
              {[
                { key: 'Roof-bearing', label: 'Roof-bearing Wall (Eave Side)', multiplier: '1.00x', description: 'Bears roof load' },
                { key: 'Gable End', label: 'Gable End Wall', multiplier: '0.75x', description: 'Non-load bearing end wall' }
              ].map((wallType) => (
                <button
                  key={wallType.key}
                  onClick={() => {setFormData({...formData, wallType: wallType.key}); nextStep();}}
                  className="w-full max-w-lg mx-auto block bg-purple-600 hover:bg-purple-700 text-white text-lg font-bold py-5 px-6 rounded-xl transition-colors"
                >
                  <div>{wallType.label}</div>
                  <div className="text-sm font-normal opacity-90">{wallType.description}</div>
                  <div className="text-xs font-normal opacity-75">Adjustment: {wallType.multiplier}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 5:
        return (
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-6">What is the existing foundation type?</h2>
            <div className="space-y-4">
              {[
                { key: 'Poured Concrete', label: 'Poured Concrete Foundation', multiplier: '1.00x' },
                { key: 'Block', label: 'Block Wall (CMU)', multiplier: '1.10x' },
                { key: 'Wood', label: 'Wood Foundation', multiplier: '0.85x' }
              ].map((foundation) => (
                <button
                  key={foundation.key}
                  onClick={() => {setFormData({...formData, foundationType: foundation.key}); nextStep();}}
                  className="w-full max-w-lg mx-auto block bg-indigo-600 hover:bg-indigo-700 text-white text-lg font-bold py-5 px-6 rounded-xl transition-colors"
                >
                  <div>{foundation.label}</div>
                  <div className="text-sm font-normal opacity-90">Foundation multiplier: {foundation.multiplier}</div>
                </button>
              ))}
            </div>
          </div>
        );

      case 6:
        return (
          <div className="text-center">
            <h2 className="text-3xl font-bold mb-8 text-green-800">🏗️ Push Pier Estimate</h2>
            
            <div className="bg-white rounded-xl shadow-xl p-8 max-w-3xl mx-auto">
              {/* Main Result */}
              <div className="bg-green-50 p-8 rounded-lg mb-8 border-2 border-green-200">
                <div className="text-2xl font-bold text-green-800 mb-4">
                  You will need <span className="text-4xl">{results.requiredPiers}</span> push piers
                </div>
                <div className="text-xl text-green-700 mb-2">
                  spaced approximately <span className="font-bold">{results.pierSpacing} feet</span> apart
                </div>
                <div className="text-lg text-green-600">
                  along the <span className="font-bold">{results.wallLength}-foot</span> wall
                </div>
              </div>

              {/* Project Details */}
              <div className="bg-blue-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-bold mb-4 text-blue-800">📋 Project Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div className="text-blue-700">
                    <strong>Wall Length:</strong> {formData.wallLength} feet
                  </div>
                  <div className="text-blue-700">
                    <strong>Stories:</strong> {formData.stories} {formData.stories === 1 ? 'story' : 'stories'}
                  </div>
                  <div className="text-blue-700">
                    <strong>Exterior Material:</strong> {formData.exteriorMaterial}
                  </div>
                  <div className="text-blue-700">
                    <strong>Wall Type:</strong> {formData.wallType}
                  </div>
                  <div className="text-blue-700">
                    <strong>Foundation Type:</strong> {formData.foundationType}
                  </div>
                  <div className="text-blue-700">
                    <strong>Pier Model:</strong> PP288 (31,200 lbs capacity)
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-bold mb-4">⚙️ Technical Calculations</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                  <div>
                    <strong>Base Load:</strong> {results.totalLoad.toLocaleString()} lbs/ft
                  </div>
                  <div>
                    <strong>Adjusted Load:</strong> {results.adjustedLoad.toLocaleString()} lbs/ft
                  </div>
                  <div>
                    <strong>Pier Rated Capacity:</strong> {results.pierCapacityRated.toLocaleString()} lbs
                  </div>
                  <div>
                    <strong>Design Load Capacity:</strong> {results.pierCapacityDesign.toLocaleString()} lbs
                  </div>
                  <div>
                    <strong>Safety Factor:</strong> 2.0x (50% design load)
                  </div>
                  <div>
                    <strong>Spacing Range:</strong> 3.5 - 6.0 feet
                  </div>
                </div>
              </div>

              {/* Notes */}
              {results.notes.length > 0 && (
                <div className="bg-yellow-50 p-6 rounded-lg mb-6">
                  <h3 className="text-xl font-bold mb-4 text-yellow-800">📝 Calculation Notes</h3>
                  <ul className="text-left space-y-2">
                    {results.notes.map((note, index) => (
                      <li key={index} className="text-yellow-800">• {note}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Disclaimer */}
              <div className="bg-orange-50 p-4 rounded-lg mb-6">
                <p className="text-orange-800 text-sm">
                  <strong>Note:</strong> Final spacing to be confirmed during install based on footing and drive depth conditions.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  onClick={resetCalculator}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-xl transition-colors mr-4"
                >
                  🔄 New Calculation
                </button>
                <button
                  onClick={onBack}
                  className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 px-8 rounded-xl transition-colors"
                >
                  ← Back to Engineering
                </button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <button 
              onClick={onBack}
              className="absolute left-4 flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Engineering
            </button>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-800">
              🏗️ Push Pier Load Calculator
            </h1>
          </div>
          <p className="text-lg text-gray-600">PP288 Pier Estimator - Foundation Support Planning</p>
          <p className="text-sm text-gray-500">Rated Capacity: 31,200 lbs | Design Capacity: 15,600 lbs (2.0x safety factor)</p>
          
          {currentStep <= 5 && (
            <div className="mt-6">
              <div className="bg-white rounded-full p-2 inline-flex items-center shadow-lg">
                <div className="flex space-x-2">
                  {[...Array(5)].map((_, i) => (
                    <div
                      key={i}
                      className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        i + 1 === currentStep 
                          ? 'bg-blue-600 text-white' 
                          : i + 1 < currentStep 
                          ? 'bg-green-600 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      {i + 1}
                    </div>
                  ))}
                </div>
                <span className="ml-4 text-gray-600 font-medium">
                  Step {currentStep} of 5
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto">
          {renderStep()}
        </div>

        {/* Navigation */}
        {currentStep > 1 && currentStep <= 5 && (
          <div className="text-center mt-8">
            <button
              onClick={prevStep}
              className="bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-6 rounded-xl transition-colors"
            >
              ← Previous
            </button>
          </div>
        )}

        {/* User Info */}
        {currentUser && (
          <div className="mt-12 text-center text-gray-500 text-sm">
            Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        </div>
      </div>
  );
};

// Engineering Home Page
const EngineeringHomePage = ({ onNavigate, onBackToGears, currentUser }) => {
  const [logoImage, setLogoImage] = React.useState(currentUser?.company?.logo_url || localStorage.getItem('groundhogsLogo'));

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white text-gray-800">
      <div className="container mx-auto px-4 py-8">
        {/* Back to Gears Button */}
        <div className="mb-6">
          <button 
            onClick={onBackToGears}
            className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Company Gears
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex flex-col items-center mb-6">
            {logoImage && (
              <div className="w-80 h-48 bg-white border border-gray-200 rounded-lg shadow-lg p-4 mb-4 flex items-center justify-center">
                <img 
                  src={logoImage} 
                  alt="Company Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
            )}
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-800 mb-4">
              ⚙️ ENGINEERING
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-gray-600 mb-2">Technical Solutions & Development Tools</p>
          <p className="text-gray-500">Engineering Access - Load Calculations & Technical Analysis</p>
        </div>

        {/* Navigation Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
          
          {/* Push Pier Load Calculator */}
          <div 
            className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
            onClick={() => onNavigate('push-pier-calculator')}
          >
            <div className="text-center">
              <div className="text-6xl mb-4">🏗️</div>
              <h2 className="text-xl font-bold mb-2 text-gray-800">PUSH PIER LOAD CALCULATOR</h2>
              <p className="text-gray-600 text-sm">Calculate pier spacing & quantity for Model 288 piers</p>
              <div className="mt-3 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full inline-block">
                Model 288 Estimator
              </div>
            </div>
          </div>

          {/* Future Engineering Tools */}
          <div className="bg-gray-100 rounded-xl p-8 opacity-75">
            <div className="text-center">
              <div className="text-6xl mb-4">📐</div>
              <h2 className="text-xl font-bold mb-2 text-gray-500">BEAM CALCULATOR</h2>
              <p className="text-gray-500 text-sm">Coming Soon</p>
            </div>
          </div>

          <div className="bg-gray-100 rounded-xl p-8 opacity-75">
            <div className="text-center">
              <div className="text-6xl mb-4">🧮</div>
              <h2 className="text-xl font-bold mb-2 text-gray-500">SOIL ANALYSIS</h2>
              <p className="text-gray-500 text-sm">Coming Soon</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        {currentUser && (
          <div className="mt-12 text-center text-gray-500 text-sm">
            Logged in as: {currentUser.name} ({currentUser.role})
          </div>
        )}
        </div>
      </div>
  );
};


export { EngineeringHomePage, PushPierCalculator };
